import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { ControllerRenderProps, FormProvider, useForm } from "react-hook-form";
import {
  ITabs,
  MuiModal,
  MuiTabPanel,
  MuiTabs,
  OptionsDataType,
  resolveContent,
  WarningConfirmationModal,
} from "../../components";
import {
  ADMIN_ROLE_IDS,
  BE_ERROR_CODES,
  PAGE_IDS,
} from "../../helpers/constants";
import { useSiteCore } from "../../hooks/useSiteCore";
import { CancelFieldModal } from "../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import { CreateUserCoachDetail } from "./components/createUserCoachDetail/CreateUserCoachDetail";
import { CreateUserRoleDetail } from "./components/createUserRoleDetail/CreateUserRoleDetail";
import { CreateUserUserDetail } from "./components/createUserUserDetail/CreateUserUserDetail";
import {
  ADD_USER_TABS,
  AddedReport,
  CandidatePortalRole,
  CoachDetailKey,
  createUserCoachDetailFormDefaultValues,
  createUserPowerBiReportsDefaultValues,
  createUserRoleFormDefaultValues,
  CreateUserRoleFormValues,
  createUserUserDetailFormDefaultValues,
  defaultServiceOffice,
  PowerBiReportsDetailKey,
  TimeZoneType,
  UserCountryType,
  UserDetailKey,
  UserRoleKey,
  ViewUserDataType,
} from "./modelsAndSchemas/models";
import {
  CreateUserCoachDetailSchema,
  CreateUserDetailSchema,
  CreateUserPowerBIReportsSchema,
  CreateUserRoleSchema,
} from "./modelsAndSchemas/ValidationSchema";
import { createUserSiteCoreKeys } from "./siteCore/SiteCoreKeys";

import { isEqual } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetCountryListForDropdowns } from "../../api/services/client/clientApis";
import {
  useCreateUserMutation,
  useEditUserMutation,
  useGetUserListById,
  useGetUserTimeZoneListForAdminUserQuery,
} from "../../api/services/manageUser/manageUserApi";
import { ConsolidatedReport } from "../../api/services/manageUser/models";
import { getFullName, getValueOrBlank, isNotEmpty } from "../../helpers/utils";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import {
  createUserDataSelector,
  manageUserRoleListSelector,
  storedEditReportListSelector,
} from "../../redux/slices/manageUser/manageUserSelector";
import { CreateUserPowerBiReportsDetail } from "./components/createUserPowerBiReportsDetail/CreateUserPowerBiReportsDetail";
import { CreateUserWrapper } from "./CreateUser.style";
import {
  isRoleTypeAll,
  isRoleTypeCirrusP3AndCandidatePortal,
  isRoleTypeClientPortal,
  sortDaysByWeekOrder,
  userSelectedRoleData,
} from "./helper";

export const pageId = PAGE_IDS.CREATE_USER;

export type PowerBiAccessFieldProps = ControllerRenderProps<
  CreateUserRoleFormValues,
  typeof UserRoleKey.hasAccessToPowerBIReport
>;

const COACH_ROLE_IDS = [
  ADMIN_ROLE_IDS.COACH,
  ADMIN_ROLE_IDS.COACH_ADMIN,
  ADMIN_ROLE_IDS.RESUME_COACH,
  ADMIN_ROLE_IDS.LEAD_CONSULTANT,
  ADMIN_ROLE_IDS.DELIVERY_COACH,
  ADMIN_ROLE_IDS.TEMPORARY_COACH_HELPDESK,
];

interface CreateUserProps {
  open: boolean;
  onClose: () => void;
  handleSuccess: () => void;
  isEditUser?: boolean;
  selectedUserId?: string;
}

export const CreateUser: FC<CreateUserProps> = ({
  open,
  onClose,
  handleSuccess,
  selectedUserId = "",
  isEditUser = false,
}) => {
  const dispatch = useDispatch();
  const userRoleList = useSelector(manageUserRoleListSelector);
  const {
    commonValidationMessages,
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    fetchDropdownValues,
    fetchRichTexts,
  } = useSiteCore();
  const { displayToast } = useToast();
  const [selectedTab, setSelectedTab] = useState(ADD_USER_TABS.ROLE_DETAIL);
  const nextTabIndexRef = useRef(ADD_USER_TABS.ROLE_DETAIL);
  const [showCancelFieldModal, setShowCancelFieldModal] = useState(false);
  const [isCoachAdmin, setIsCoachAdmin] = useState(false);
  const [hasPowerBIAccess, setHasPowerBIAccess] = useState(false);
  const [addedReports, setAddedReports] = useState<AddedReport[]>([]);
  const [showChronofyPopup, setShowChronofyPopup] = useState(false);

  const [showEditConfirmationModal, setShowEditConfirmationModal] =
    useState(false);
  const [isPowerBIReportInEditMode, setIsPowerBIReportInEditMode] =
    useState<boolean>(false);
  const storedUserDetail = useSelector(createUserDataSelector);
  const storedAddedReports: ConsolidatedReport[] = useSelector(
    storedEditReportListSelector,
  );
  const { loggedInUser } = useGetLoggedInUserDetails();
  const [showPowerBiAccessModal, setShowPowerBiAccessModal] =
    useState<boolean>(false);
  const [showCoachRemoveConfirm, setShowCoachRemoveConfirm] =
    useState<boolean>(false);
  const [isCoachResetConfirmed, setIsCoachResetConfirmed] =
    useState<boolean>(false);
  const [previousCoachRoles, setPreviousCoachRoles] = useState<string[]>([]);

  const roleRef = useRef<{ resetValues: (values?: string[]) => void }>(null);
  const availableDayRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const pendingCheckboxRef = useRef<{
    value: boolean;
    fieldProps: PowerBiAccessFieldProps;
  } | null>(null);
  const {
    data: manageUserCountryList,
    isLoading: isManageUserCountryListLoading,
    isError: isManageUserCountryListError,
  } = useGetCountryListForDropdowns();

  const {
    isPending: isCreateUserLoading,
    isError: isCreateUserError,
    mutateAsync: createUserMutation,
    isSuccess: isCreateUserSuccess,
    error: createUserError,
    data: createUserData,
  } = useCreateUserMutation();

  const {
    isPending: isEditUserLoading,
    isError: isEditUserError,
    mutateAsync: editUserMutation,
    isSuccess: isEditUserSuccess,
    data: editUserDataToSave,
  } = useEditUserMutation();

  const {
    data: userTimeZoneList,
    isLoading: isUserTimeZoneListLoading,
    isError: isUserTimeZoneListError,
  } = useGetUserTimeZoneListForAdminUserQuery();

  const {
    isFetching: isUserDataPending,
    data: editUserData,
    isError: isUserDataError,
    refetchWithParams: refetchUserData,
  } = useGetUserListById();

  const geoTypeOptionsSiteCore = fetchDropdownValues(
    createUserSiteCoreKeys.powerBIReportsKey,
    createUserSiteCoreKeys.geoTypeList,
  );

  useEffect(() => {
    if (isNotEmpty(selectedUserId)) {
      refetchUserData(selectedUserId);
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (
      isManageUserCountryListError ||
      isUserTimeZoneListError ||
      isUserDataError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ createUser: { isVisible: false } }));
    }
  }, [isManageUserCountryListError, isUserTimeZoneListError, isUserDataError]);

  useEffect(() => {
    if (isCreateUserError || isEditUserError) {
      displayToast(
        createUserError?.response?.data?.errorMessage ??
          commonLabels.someThingWentWrongPleaseTryAgainLater,
        "error",
      );
      dispatch(toggleLoader({ createUser: { isVisible: false } }));
    }
  }, [isCreateUserError, isEditUserError]);

  useEffect(() => {
    if (isCreateUserSuccess || isEditUserSuccess) {
      let userData = isEditUserSuccess ? editUserDataToSave : createUserData;
      if (
        userData &&
        isNotEmpty(userData.errorCode) &&
        userData.errorCode === BE_ERROR_CODES.USER_SAVED_BUT_CRONOFY_FAILED
      ) {
        setShowChronofyPopup(true);
      } else {
        displayToast(
          fetchLabel(
            createUserSiteCoreKeys.userRoleKey,
            isCreateUserSuccess
              ? createUserSiteCoreKeys.userAddedToastMessage
              : createUserSiteCoreKeys.userDetailsUpdatedToastMessage,
          ),
        );
        handleSuccess();
      }
    }
  }, [isCreateUserSuccess, isEditUserSuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        createUser: {
          isVisible:
            isManageUserCountryListLoading ||
            isUserTimeZoneListLoading ||
            isCreateUserLoading ||
            isUserDataPending ||
            isEditUserLoading,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ createUser: { isVisible: false } }));
    };
  }, [
    isManageUserCountryListLoading,
    isUserTimeZoneListLoading,
    isCreateUserLoading,
    isUserDataPending,
    isEditUserLoading,
  ]);

  const roleDetailMethods = useForm({
    mode: "all",
    defaultValues: createUserRoleFormDefaultValues,
    resolver: yupResolver(CreateUserRoleSchema(commonValidationMessages)),
  });

  const {
    formState: { isDirty },
    handleSubmit: handleSubmitRoleDetail,
    getValues: getRoleDetailValues,
    watch: watchRoleDetail,
    setValue: setRoleDetailValue,
    clearErrors: clearRoleDetailsErrors,
  } = roleDetailMethods;

  const userDetailMethods = useForm({
    mode: "all",
    defaultValues: createUserUserDetailFormDefaultValues,
    resolver: yupResolver(CreateUserDetailSchema(commonValidationMessages)),
  });

  const powerBIReportsMethod = useForm({
    mode: "all",
    defaultValues: createUserPowerBiReportsDefaultValues,
    resolver: yupResolver(
      CreateUserPowerBIReportsSchema(commonValidationMessages),
    ),
  });

  const {
    formState: { isDirty: isDirtyUserDetail },
    handleSubmit: handleSubmitUserDetail,
    setValue: setUserDetailValue,
    getValues: getUserDetailValues,
    reset: resetUserDetail,
  } = userDetailMethods;

  // User detail tab may never mount when jumping directly to a later tab
  useEffect(() => {
    if (isEditUser && isNotEmpty(editUserData)) {
      resetUserDetail({
        ...createUserUserDetailFormDefaultValues,
        [UserDetailKey.preferredName]: getValueOrBlank(editUserData?.username),
        [UserDetailKey.userEmail]: getValueOrBlank(editUserData?.emailAddress),
        [UserDetailKey.affliateEmailId]: getValueOrBlank(
          editUserData?.affiliateEmailAddress,
        ),
        [UserDetailKey.userCountry]: getValueOrBlank(editUserData?.countryCode),
        [UserDetailKey.homeOffice]: getValueOrBlank(editUserData?.officeId),
        [UserDetailKey.applicationLanguage]: getValueOrBlank(
          editUserData?.applicationLanguageCode,
        ),
        [UserDetailKey.timeZone]: getValueOrBlank(editUserData?.timeZone),
        [UserDetailKey.userStatus]: editUserData?.isActive
          ? createUserSiteCoreKeys.statusActive
          : createUserSiteCoreKeys.statusInActive,
        [UserDetailKey.dateFormat]: getValueOrBlank(
          editUserData?.userDateFormat?.id,
        ),
      });
    }
  }, [editUserData, isEditUser]);

  const {
    formState: { isDirty: isPowerBIReportsDirty },
    setValue: setPowerBIReportsValue,
    getValues: getPowerBIReportsValue,
    reset: resetPowerBiReportsForm,
    setError: setPowerBIReportsError,
  } = powerBIReportsMethod;

  const coachDetailMethods = useForm({
    mode: "all",
    defaultValues: createUserCoachDetailFormDefaultValues,
    resolver: yupResolver(
      CreateUserCoachDetailSchema({
        ...commonValidationMessages,
        startTimeEndTimeValidationMessage: fetchLabel(
          createUserSiteCoreKeys.coachDetailsKey,
          createUserSiteCoreKeys.startTimeEndTimeValidationMessage,
        ),
      }),
    ),
  });

  const {
    formState: { isDirty: isDirtyCoachDetail },
    handleSubmit: handleSubmitCoachDetail,
    getValues: getCoachDetailValues,
    reset: resetCoachDetail,
  } = coachDetailMethods;

  // Coach tab may never mount when jumping directly to a later tab
  useEffect(() => {
    if (!isEditUser || !isNotEmpty(editUserData) || isDirtyCoachDetail) return;
    const weeklyPeriods: NonNullable<
      NonNullable<ViewUserDataType["schedulingAvailability"]>["weekly_periods"]
    > = editUserData?.schedulingAvailability?.weekly_periods ?? [];
    const availableDays = sortDaysByWeekOrder(
      weeklyPeriods
        .map((period) => period.day)
        .filter((day): day is string => day !== undefined),
    );
    resetCoachDetail({
      ...createUserCoachDetailFormDefaultValues,
      [CoachDetailKey.expertise]: getValueOrBlank(editUserData?.expertise),
      [CoachDetailKey.bioData]: getValueOrBlank(editUserData?.bio),
      [CoachDetailKey.timeZone]: getValueOrBlank(
        editUserData?.schedulingAvailability?.tzid,
      ),
      [CoachDetailKey.linkedInId]: getValueOrBlank(
        editUserData?.socialIdDetails,
      ),
      [CoachDetailKey.availableDays]: availableDays,
      [CoachDetailKey.serviceAccountName]: getValueOrBlank(
        editUserData?.serviceAccountId,
      ),
      [CoachDetailKey.workingHoursEndTime]: getValueOrBlank(
        weeklyPeriods[0]?.end_time,
      ),
      [CoachDetailKey.workingHoursStartTime]: getValueOrBlank(
        weeklyPeriods[0]?.start_time,
      ),
      [CoachDetailKey.coachingLanguages]: getValueOrBlank(
        editUserData?.language,
      ),
    });
  }, [editUserData, isEditUser]);

  const getUserLabel = (key: string) =>
    fetchLabel(createUserSiteCoreKeys.userDetailKey, key);

  const getTabIndex = (tabKey: string) =>
    renderedTabs.findIndex((tab) => tab.id === getUserLabel(tabKey));

  const syncPowerBiUserValues = () => {
    const { userEmail, firstName, lastName } = getRoleDetailValues();
    setPowerBIReportsValue(PowerBiReportsDetailKey.userEmail, userEmail);
    setPowerBIReportsValue(PowerBiReportsDetailKey.firstName, firstName);
    setPowerBIReportsValue(PowerBiReportsDetailKey.lastName, lastName);
  };

  const setPowerBiReportsRequiredError = () =>
    setPowerBIReportsError(PowerBiReportsDetailKey.reports, {
      type: "manual",
      message: commonValidationMessages.requiredValidation,
    });

  const handleModalClose = () => {
    if (
      !isDirty &&
      !isDirtyUserDetail &&
      !isDirtyCoachDetail &&
      !isPowerBIReportsDirty
    )
      return onClose();
    setShowCancelFieldModal(true);
  };

  const handlePowerBIEditIndexChange = (index: number | null) => {
    setIsPowerBIReportInEditMode(index !== null);
  };

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    if (value < selectedTab) {
      nextTabIndexRef.current = value;
      if (hasPowerBIAccess) {
        handleGoToPowerBiReportsTab();
      }
      setSelectedTab(value);
    } else {
      handleSubmit(value);
    }
  };

  const handleGoToUserDetailTab = () => {
    setSelectedTab(
      isEditUser ? nextTabIndexRef.current : ADD_USER_TABS.USER_DETAIL,
    );
    const { userEmail } = getRoleDetailValues();
    setUserDetailValue(UserRoleKey.userEmail, userEmail);
  };

  const handleGoToPowerBiReportsTab = () => {
    const powerBiTabIndex = getTabIndex(
      createUserSiteCoreKeys.powerBIReportsTab,
    );

    if (powerBiTabIndex === -1) return;

    setSelectedTab(isEditUser ? nextTabIndexRef.current : powerBiTabIndex);
    syncPowerBiUserValues();
  };

  const buildAdminUserRoles = (
    roleType: string | undefined,
    role: string[] | undefined,
    clientPortalRole: string | undefined,
  ) => {
    const clientPortalRoleText = getValueOrBlank(
      storedUserDetail?.clientPortalRoleText,
    );
    const hasCandidateRole =
      (isRoleTypeAll(roleType) ||
        isRoleTypeCirrusP3AndCandidatePortal(roleType)) &&
      role?.length;
    const hasClientRole =
      (isRoleTypeAll(roleType) || isRoleTypeClientPortal(roleType)) &&
      clientPortalRole &&
      isNotEmpty(clientPortalRoleText);

    return {
      ...(hasCandidateRole && {
        candidatePortalRole: userSelectedRoleData(role, userRoleList),
      }),
      ...(hasClientRole && {
        clientPortalRole: {
          //api change - api expecting name also
          role: clientPortalRoleText,
          roleId: clientPortalRole,
        },
      }),
      // we have service office as string array in the form, but API expects an array of objects
      serviceOffices: storedUserDetail.serviceOfficeList ?? [],
    };
  };

  const buildCoachData = () => {
    const {
      bioData,
      expertise,
      coachingLanguages,
      linkedInId,
      serviceAccountName,
      timeZone,
      workingHoursStartTime,
      workingHoursEndTime,
      availableDays,
    } = getCoachDetailValues();

    return {
      Bio: bioData,
      Expertise: expertise,
      Language: coachingLanguages,
      SocialIdDetails: linkedInId,
      schedulingAvailability: {
        tzid: timeZone,
        weekly_periods:
          availableDays?.map((day: string) => ({
            day,
            start_time: workingHoursStartTime,
            end_time: workingHoursEndTime,
          })) ?? [],
      },
      ServiceAccountId: serviceAccountName,
    };
  };

  const buildReportsData = (defaultGeoType?: string | number) => ({
    comments: getPowerBIReportsValue().comments,
    powerBIReports: addedReports.map((r) => ({
      reportId: r.reportId,
      globalIndicator: defaultGeoType,
      salesRegionId: r.salesRegion,
      marketIds: r.salesMarket,
    })),
  });

  const prepareApiData = () => {
    const defaultGeoType = geoTypeOptionsSiteCore[0]?.value;
    if (
      hasPowerBIAccess &&
      addedReports.length &&
      !isNotEmpty(defaultGeoType)
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      return null;
    }

    const {
      firstName,
      lastName,
      userEmail,
      role,
      clientPortalRole,
      activeDirectoryId,
      hasAccessToPowerBIReport,
      roleType,
    } = getRoleDetailValues();

    const {
      preferredName,
      affliateEmailId,
      applicationLanguage,
      userCountry,
      homeOffice,
      timeZone,
      userStatus,
    } = getUserDetailValues();

    return {
      ActiveDirectoryId: activeDirectoryId,
      FirstName: firstName,
      LastName: lastName,
      AffiliateEmailAddress: affliateEmailId,
      CountryCode: userCountry,
      EmailAddress: userEmail,
      timeZone: timeZone,
      ApplicationLanguage: applicationLanguage,
      IsActive: userStatus === createUserSiteCoreKeys.statusActive,
      IsEnterpriseAdmin: false,
      ContactNo: "",

      OfficeId: homeOffice,
      OfficeName: getValueOrBlank(
        storedUserDetail?.homeOfficeText || editUserData?.officeName,
      ),
      AdminUserRoles: buildAdminUserRoles(roleType, role, clientPortalRole),
      Username: preferredName,
      CreatedBy: `${getValueOrBlank(loggedInUser?.lastName)} ${getValueOrBlank(loggedInUser?.firstName)}`,
      // default values for service office
      ...defaultServiceOffice,

      ...(isCoachAdmin && buildCoachData()),
      hasAccessToPowerBIReport,
      ...(hasPowerBIAccess && { reports: buildReportsData(defaultGeoType) }),
      IsCalledByP3: true,
      dateFormatId: getUserDetailValues().dateFormat,
    };
  };

  const validatePowerBiTab = async () => {
    // The reports field is emptied after each add, so validity is the added reports table
    if (!addedReports.length) {
      setPowerBiReportsRequiredError();
      return false;
    }
    powerBIReportsMethod.clearErrors(PowerBiReportsDetailKey.reports);
    return powerBIReportsMethod.trigger([PowerBiReportsDetailKey.comments]);
  };

  // Validates every rendered form and navigates to the first invalid tab
  const validateAllForms = async () => {
    const checks = [
      {
        tabLabel: createUserSiteCoreKeys.roleDetailsTab,
        validate: () => roleDetailMethods.trigger(),
        isVisible: true,
      },
      {
        tabLabel: createUserSiteCoreKeys.userDetailsTab,
        validate: () => userDetailMethods.trigger(),
        isVisible: true,
      },
      {
        tabLabel: createUserSiteCoreKeys.coachDetailsTab,
        validate: () => coachDetailMethods.trigger(),
        isVisible: isCoachAdmin,
      },
      {
        tabLabel: createUserSiteCoreKeys.powerBIReportsTab,
        validate: validatePowerBiTab,
        isVisible: hasPowerBIAccess,
      },
    ].filter((c) => c.isVisible);

    const results = await Promise.all(checks.map((c) => c.validate()));
    const invalidIndex = results.findIndex((isValid) => !isValid);
    if (invalidIndex === -1) return true;

    const tabIndex = getTabIndex(checks[invalidIndex].tabLabel);
    if (tabIndex !== -1) {
      setSelectedTab(tabIndex);
      // Deferred so it runs after the tab mounts; mount-time form resets clear errors
      setTimeout(checks[invalidIndex].validate, 200);
    }
    return false;
  };

  const handleCreateUserApi = async () => {
    if (!(await validateAllForms())) return;
    if (isEditUser) {
      setShowEditConfirmationModal(true);
    } else {
      const apiData = prepareApiData();
      if (apiData) createUserMutation(apiData);
    }
  };

  const handleEditConfirmationModal = () => {
    const apiData = isEditUser ? prepareApiData() : null;
    if (!apiData) return;
    editUserMutation({
      StaffId: selectedUserId,
      ...apiData,
    });
  };

  const handleSubmit = (nextIndex: number) => {
    const currentTabId = renderedTabs[selectedTab].id;
    nextTabIndexRef.current = nextIndex;

    const goAfterUserDetail = () => {
      if (isCoachAdmin) {
        setSelectedTab(
          isEditUser ? nextTabIndexRef.current : ADD_USER_TABS.COACH_DETAIL,
        );
      } else if (hasPowerBIAccess) {
        handleGoToPowerBiReportsTab();
      } else {
        handleCreateUserApi();
      }
    };

    const submitHandlers: Record<string, () => void> = {
      [getUserLabel(createUserSiteCoreKeys.roleDetailsTab)]: () =>
        handleSubmitRoleDetail(handleGoToUserDetailTab)(),
      [getUserLabel(createUserSiteCoreKeys.userDetailsTab)]: () =>
        handleSubmitUserDetail(goAfterUserDetail)(),
      [getUserLabel(createUserSiteCoreKeys.coachDetailsTab)]: () =>
        handleSubmitCoachDetail(
          hasPowerBIAccess ? handleGoToPowerBiReportsTab : handleCreateUserApi,
        )(),
      [getUserLabel(createUserSiteCoreKeys.powerBIReportsTab)]: () =>
        addedReports.length
          ? handleCreateUserApi()
          : setPowerBiReportsRequiredError(),
    };

    submitHandlers[currentTabId]?.();
  };

  const countryListOptions = useMemo<OptionsDataType[]>(
    () =>
      manageUserCountryList?.map((country: UserCountryType) => ({
        label: country.title,
        value: country.id,
      })) ?? [],
    [manageUserCountryList],
  );

  const timeZoneListOptions = useMemo<OptionsDataType[]>(
    () =>
      userTimeZoneList?.map((timeZone: TimeZoneType) => ({
        label: timeZone.title,
        value: timeZone.id,
      })) ?? [],
    [userTimeZoneList],
  );

  const handleBackButtonClick = () => {
    setSelectedTab((prev) => prev - 1);
  };

  useEffect(() => {
    const { role } = getRoleDetailValues();
    if (isNotEmpty(role)) {
      const isCoachOrCoachAdmin = COACH_ROLE_IDS.some((id) =>
        role?.includes(id),
      );

      setIsCoachAdmin(isCoachOrCoachAdmin);
      checkPreviousCoachRoles(isCoachOrCoachAdmin);
      handleRemoveCoach(isCoachOrCoachAdmin);
    } else {
      setIsCoachAdmin(false);
    }
  }, [watchRoleDetail(UserRoleKey.role)]);

  const checkPreviousCoachRoles = (isCoachOrCoachAdmin: boolean) => {
    const hadCoachRoleBefore = (
      editUserData?.adminUserRoles?.candidatePortalRole ?? []
    ).some(
      (r: CandidatePortalRole) =>
        r.roleId !== undefined &&
        [ADMIN_ROLE_IDS.COACH, ADMIN_ROLE_IDS.COACH_ADMIN].includes(r.roleId),
    );
    // hadCoachRoleBefore checks if the user had coach role when the edit modal was opened
    // if roles is not there and user try to update role as coach roles then storing those roles locally to get back
    if (!hadCoachRoleBefore && isCoachOrCoachAdmin) {
      const { role } = getRoleDetailValues();

      const localRoles = [...previousCoachRoles];
      if (
        !localRoles.includes(ADMIN_ROLE_IDS.COACH) &&
        role?.includes(ADMIN_ROLE_IDS.COACH)
      ) {
        localRoles.push(ADMIN_ROLE_IDS.COACH);
      }
      if (
        !localRoles.includes(ADMIN_ROLE_IDS.COACH_ADMIN) &&
        role?.includes(ADMIN_ROLE_IDS.COACH_ADMIN)
      ) {
        localRoles.push(ADMIN_ROLE_IDS.COACH_ADMIN);
      }

      setPreviousCoachRoles(localRoles);
    }
  };

  const handleRemoveCoach = (isCoachOrCoachAdmin: boolean) => {
    if (
      isEditUser &&
      isDirtyCoachDetail &&
      !isCoachOrCoachAdmin &&
      !showCoachRemoveConfirm
    ) {
      setShowCoachRemoveConfirm(true);
    }
  };

  useEffect(() => {
    if (
      isEditUser &&
      isNotEmpty(editUserData) &&
      previousCoachRoles.length === 0 // store only once
    ) {
      const initialCoachRoles = (
        editUserData?.adminUserRoles?.candidatePortalRole ?? []
      )
        .filter(
          (r: CandidatePortalRole) =>
            r.roleId !== undefined &&
            [ADMIN_ROLE_IDS.COACH, ADMIN_ROLE_IDS.COACH_ADMIN].includes(
              r.roleId,
            ),
        )
        .map((r: CandidatePortalRole) => r.roleId);
      setPreviousCoachRoles(initialCoachRoles); //storing coach roles while edit mode
    }
  }, [editUserData, isEditUser]);

  const handleEditCoachConfirmationModal = () => {
    setShowCoachRemoveConfirm(false);
    setIsCoachResetConfirmed(true);
  };

  const handleEditCoachCancel = () => {
    setShowCoachRemoveConfirm(false);
    setIsCoachResetConfirmed(false);

    const { role: currentRoles } = getRoleDetailValues();

    // merge without duplicates
    const mergedRoles = Array.from(
      new Set([...(currentRoles ?? []), ...previousCoachRoles]),
    );

    clearRoleDetailsErrors(UserRoleKey.role);
    setRoleDetailValue(UserRoleKey.role, mergedRoles, { shouldTouch: false });
    roleRef.current?.resetValues(mergedRoles);
    setPreviousCoachRoles([]); //resetting previous coach roles
  };

  useEffect(() => {
    const { hasAccessToPowerBIReport } = getRoleDetailValues();
    setHasPowerBIAccess(!!hasAccessToPowerBIReport);
  }, [watchRoleDetail(UserRoleKey.hasAccessToPowerBIReport)]);

  const modalTitle = useMemo(() => {
    if (isEditUser) {
      return `${fetchLabel(
        createUserSiteCoreKeys.userDetailKey,
        createUserSiteCoreKeys.editUserHeader,
      )} - ${getFullName(editUserData?.firstName, editUserData?.lastName, true)}`;
    }
    return fetchComponentTitle(createUserSiteCoreKeys.userDetailKey);
  }, [isEditUser, editUserData]);

  function isReportTableUpdated(
    reduxData: ConsolidatedReport[],
    localData: AddedReport[],
  ): boolean {
    return !isEqual(reduxData, localData);
  }

  const onPowerBiAccessCheckboxChange = (
    checked: boolean,
    fieldProps: PowerBiAccessFieldProps,
  ) => {
    const isReportTableDirty = isReportTableUpdated(
      storedAddedReports,
      addedReports,
    );

    if (
      (isPowerBIReportsDirty || isReportTableDirty) &&
      !checked &&
      isEditUser
    ) {
      pendingCheckboxRef.current = { value: checked, fieldProps };
      setShowPowerBiAccessModal(true);
    } else {
      fieldProps.onChange(checked);
    }
  };

  const handlePowerBiModalConfirm = () => {
    resetPowerBiReportsForm();
    if (pendingCheckboxRef.current) {
      pendingCheckboxRef.current.fieldProps.onChange(
        pendingCheckboxRef.current.value,
      );
    }
    pendingCheckboxRef.current = null;
    setAddedReports([]);
    setShowPowerBiAccessModal(false);
  };

  const tabConfigs = [
    {
      tabKey: createUserSiteCoreKeys.roleDetailsTab,
      isVisible: true,
      content: (
        <FormProvider {...roleDetailMethods}>
          <CreateUserRoleDetail
            pageId={pageId}
            countryListOptions={countryListOptions}
            selectedTab={selectedTab}
            isEditUser={isEditUser}
            editUserData={editUserData}
            onPowerBiAccessCheckboxChange={onPowerBiAccessCheckboxChange}
            isCoachAdmin={isCoachAdmin}
            hasPowerBIAccess={hasPowerBIAccess}
            roleRef={roleRef}
          />
        </FormProvider>
      ),
    },
    {
      tabKey: createUserSiteCoreKeys.userDetailsTab,
      isVisible: true,
      content: (
        <FormProvider {...userDetailMethods}>
          <CreateUserUserDetail
            pageId={pageId}
            countryListOptions={countryListOptions}
            timeZoneListOptions={timeZoneListOptions}
            isEditUser={isEditUser}
            selectedTab={selectedTab}
          />
        </FormProvider>
      ),
    },
    {
      tabKey: createUserSiteCoreKeys.coachDetailsTab,
      isVisible: isCoachAdmin,
      content: (
        <FormProvider {...coachDetailMethods}>
          <CreateUserCoachDetail
            pageId={pageId}
            countryCode={getUserDetailValues().userCountry}
            editUserData={editUserData}
            isEditUser={isEditUser}
            isCoachAdmin={isCoachAdmin}
            availableDayRef={availableDayRef}
            isCoachResetConfirmed={isCoachResetConfirmed}
          />
        </FormProvider>
      ),
    },
    {
      tabKey: createUserSiteCoreKeys.powerBIReportsTab,
      isVisible: hasPowerBIAccess,
      content: (
        <FormProvider {...powerBIReportsMethod}>
          <CreateUserPowerBiReportsDetail
            pageId={pageId}
            isEditUser={isEditUser}
            editUserData={editUserData}
            addedReports={addedReports}
            setAddedReports={setAddedReports}
            handlePowerBIEditIndexChange={handlePowerBIEditIndexChange}
          />
        </FormProvider>
      ),
    },
  ];

  const USER_TABS: ITabs[] = tabConfigs
    .filter((tab) => tab.isVisible)
    .map(({ tabKey, content }) => ({
      id: getUserLabel(tabKey),
      label: getUserLabel(tabKey),
      content,
    }));

  const renderedTabs = USER_TABS.map((tab, i) => ({
    ...tab,
    testId: `${pageId}-tab${i + 1}`,
    index: i,
  }));

  const primaryButtonTitle = useMemo(() => {
    const isLastTab = selectedTab === renderedTabs.length - 1;

    if (isLastTab) {
      return isEditUser ? commonLabels.update : commonLabels.save;
    }
    return commonLabels.next;
  }, [selectedTab, renderedTabs.length, isEditUser]);

  const isSelectedTabPowerBI =
    renderedTabs[selectedTab]?.id ===
    getUserLabel(createUserSiteCoreKeys.powerBIReportsTab);

  // Tabs can be jumped to directly, bypassing handleGoToPowerBiReportsTab
  useEffect(() => {
    if (!isSelectedTabPowerBI) return;
    syncPowerBiUserValues();
  }, [isSelectedTabPowerBI]);

  return (
    <MuiModal
      onClose={handleModalClose}
      title={modalTitle}
      open={open}
      primaryButtonTitle={primaryButtonTitle}
      disablePrimaryButton={
        isSelectedTabPowerBI ? isPowerBIReportInEditMode : false
      }
      disableActionButton={
        isSelectedTabPowerBI ? isPowerBIReportInEditMode : false
      }
      disableSecondaryButton={
        isSelectedTabPowerBI ? isPowerBIReportInEditMode : false
      }
      onPrimaryButtonClick={() => handleSubmit(selectedTab + 1)}
      onSecondaryButtonClick={handleModalClose}
      secondaryButtonTitle={commonLabels.cancel}
      hasLargeContent
      actionButtonTitle={
        selectedTab > ADD_USER_TABS.ROLE_DETAIL ? commonLabels.back : ""
      }
      onActionButtonClick={handleBackButtonClick}
      className="add-user-modal"
    >
      <CreateUserWrapper>
        <MuiTabs
          tabs={renderedTabs}
          value={selectedTab}
          onChange={onTabChange}
          tabsTestIds={renderedTabs.map((tab) => tab.id)}
        >
          {renderedTabs.map(({ content, testId, index, label }) => (
            <MuiTabPanel
              key={label}
              panelId={renderedTabs[selectedTab].id}
              ariaLabelledById={`${testId}-tab`}
              value={selectedTab}
              index={index}
              isPanelWithMount
            >
              {resolveContent(content)}
            </MuiTabPanel>
          ))}
        </MuiTabs>

        {showCancelFieldModal && (
          <CancelFieldModal
            showModal={showCancelFieldModal}
            handleModalClose={() => setShowCancelFieldModal(false)}
            handlePrimaryButtonClick={onClose}
            cancelMessage={fetchLabel(
              createUserSiteCoreKeys.userRoleKey,
              createUserSiteCoreKeys.addUserCancelConfirmationMessage,
            )}
          />
        )}

        {showEditConfirmationModal && (
          <WarningConfirmationModal
            show={showEditConfirmationModal}
            modalTitle={commonLabels.confirmation}
            onClose={() => setShowEditConfirmationModal(false)}
            onPrimaryButtonClick={handleEditConfirmationModal}
            labelContent={fetchLabel(
              createUserSiteCoreKeys.userRoleKey,
              createUserSiteCoreKeys.updateUserConfirmationMessage,
            )}
            secondaryButtonTitle={commonLabels.no}
          />
        )}

        {showPowerBiAccessModal && (
          <WarningConfirmationModal
            show={showPowerBiAccessModal}
            modalTitle={commonLabels.confirmation}
            onClose={() => setShowPowerBiAccessModal(false)}
            onPrimaryButtonClick={handlePowerBiModalConfirm}
            labelContent={fetchLabel(
              createUserSiteCoreKeys.userRoleKey,
              createUserSiteCoreKeys.powerBIReportAccessRevokeMessage,
            )}
            secondaryButtonTitle={commonLabels.no}
          />
        )}

        {showCoachRemoveConfirm && (
          <WarningConfirmationModal
            show={showCoachRemoveConfirm}
            modalTitle={commonLabels.confirmation}
            onClose={handleEditCoachCancel}
            onPrimaryButtonClick={handleEditCoachConfirmationModal}
            labelContent={fetchLabel(
              createUserSiteCoreKeys.userRoleKey,
              createUserSiteCoreKeys.coachRoleRevokeMessage,
            )}
            secondaryButtonTitle={commonLabels.no}
          />
        )}

        {showChronofyPopup && (
          <WarningConfirmationModal
            show={showChronofyPopup}
            modalTitle={commonLabels.success}
            onClose={() => {
              setShowChronofyPopup(false);
              handleSuccess();
            }}
            onPrimaryButtonClick={() => {
              setShowChronofyPopup(false);
              handleSuccess();
            }}
            primaryButtonTitle={commonLabels.ok}
            showSecondaryButton={false}
            labelContent={
              isEditUser
                ? fetchRichTexts(
                    createUserSiteCoreKeys.userRoleKey,
                    createUserSiteCoreKeys.userUpdatedNotUpdatedInCronofy,
                  )
                : fetchRichTexts(
                    createUserSiteCoreKeys.userRoleKey,
                    createUserSiteCoreKeys.userCreatedNotUpdatedInCronofy,
                  )
            }
            isLabelHTMLContent
          />
        )}
      </CreateUserWrapper>
    </MuiModal>
  );
};
